import { useGetAuction, useGetPatientQuery } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { useSenderAccountContext } from "context/SenderAccountContext";
import { PATIENT_TAB_SEARCH } from "core/consts";
import { hasAccessToDoctorDashboard } from "core/model/accounts";
import { useIntParams } from "core/model/utils/urls";
import { Auction, CareseekerRole, DeepPartial, Patient } from "core/types";
import { BaseEventSchema } from "core/validationSchemas/tracking";
import SpinnerPage from "ds_legacy/components/Spinner";
import { RedirectButton } from "dsl/atoms/RedirectButton";
import PatientProfile from "dsl/ecosystems/PatientProfile";
import { useCareseekerNavigationHandlers, usePageMetaData } from "dsl/hooks";
import { PatientEncryptionProvider } from "dsl/organisms/Encryption/Patient";
import { defaultRedirectPath } from "entries/acp/Router";
import { Suspense, useCallback, useEffect } from "react";
import {
  Navigate,
  Outlet,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useTracking } from "react-tracking";
import {
  useAppDispatch,
  useAppSelector,
  useConnecterWithCareseeker,
  useLoggedInAccount,
} from "reduxentities/hooks";
import { selectCareseekerRoles } from "reduxentities/selectors";
import { CARESEEKER_CHANGED } from "reduxentities/slices/authSlice";
import { useTranslations } from "translations";
import { doctorDashboardRedirectPath } from "./paths";

export type PatientContext = {
  auction?: Auction;
  patient: Patient;
};

/** Patient context with auction always set (auction-scoped routes). */
export type PatientAuctionContext = PatientContext & { auction: Auction };

export function usePatientContext(): PatientContext {
  const context = useOutletContext<PatientContext>();
  if (!context) {
    throw new Error(
      "usePatientContext must be used within a PatientRouteLayout",
    );
  }
  return context;
}

function usePatientPageTitle(path: string, patient: Patient | undefined) {
  const translations = useTranslations();

  const patientIdentifier = patient?.user_id;
  const getNewTitle = useCallback(
    (titleBase: string) =>
      `${translations.patient.patient} ${
        patientIdentifier ? `${patientIdentifier}` : ""
      }${titleBase ? ` ${titleBase}` : ""}`,
    [patientIdentifier],
  );

  const pageMap: { [key: string]: string } = {
    profile: translations.patient.profile,
    search: translations.search.search,
    timeline: translations.timeline.title,
  };

  const pageKey = Object.keys(pageMap).find((key) => path.includes(key));

  usePageMetaData({
    title: () => getNewTitle((pageKey && pageMap[pageKey]) ?? ""),
  });
}

function NoAuctionsRedirectToNewAuction({
  externalId,
  patientId,
}: {
  externalId?: string;
  patientId: number | undefined;
}) {
  const account = useLoggedInAccount();
  const navigationHandlers = useCareseekerNavigationHandlers();

  useEffect(() => {
    console.error(
      `No auctions redirect - account ${account?.id} - patient ${patientId}`,
    );
    navigationHandlers.patient.new({ external_id: externalId });
  }, []);

  return null;
}

export function PatientProfileIndex() {
  const { patient } = useOutletContext<PatientContext>();

  if (!patient?.auctions?.length) {
    return (
      <NoAuctionsRedirectToNewAuction
        externalId={patient?.external_id}
        patientId={patient?.id}
      />
    );
  }

  const defaultAuction = patient.auctions[0];
  const auctionWithPatient: Auction = {
    ...defaultAuction,
    patient,
  };

  return <PatientProfile auction={auctionWithPatient} />;
}

/** Loads auction by ID and renders the outlet with auction+patient context for patient/.../auction/:auctionId/* routes. */
export function PatientAuctionOutlet({
  auctionId,
  patient,
}: {
  auctionId: number;
  patient: Patient;
}) {
  const translations = useTranslations();
  const { careseekerId } = useConnecterWithCareseeker();
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectCareseekerRoles);
  const [queryProgress, auction] = useGetAuction(auctionId);

  usePatientPageTitle(location.pathname, patient);

  useEffect(() => {
    const patientCareseekerId = patient.careseeker?.id;
    if (patientCareseekerId === careseekerId) return;

    const country = patient.careseeker?.address?.country;
    const inRoles = roles?.some(
      (role: CareseekerRole) => role?.careseeker?.id === patientCareseekerId,
    );

    if (inRoles) {
      dispatch(
        CARESEEKER_CHANGED({
          careseeker_id: patientCareseekerId,
          careseeker_name: patient.careseeker?.name,
          country,
        }),
      );
    }
  }, [patient.id, careseekerId, dispatch, patient, roles]);

  return (
    <Result
      data={auction}
      ErrorComponent={
        <RedirectButton
          label={translations.actions.backToDashboard}
          url="/dashboard/todo/active"
        />
      }
      getErrorLabel={(translations) => translations.patient.errorLoading}
      id="patient-route-auction"
      queryProgress={queryProgress}
    >
      {(auction) => {
        return (
          <PatientEncryptionProvider patient={patient}>
            <Outlet context={{ auction, patient }} />
          </PatientEncryptionProvider>
        );
      }}
    </Result>
  );
}

/**
 * Top-level layout for patient-scoped routes (patient/:patientId/...).
 * Single outlet with context { patient, auction? }. Resolves patient from URL,
 * applies role-based layout (doctor vs clinic), handles redirects, and wraps
 * content in tracking + encryption.
 */
export default function PatientRouteLayout() {
  const translations = useTranslations();
  const [searchParams] = useSearchParams();
  const { auctionId, consultationId, patientId } = useIntParams<{
    auctionId: number | undefined;
    patientId: number;
    consultationId: number | undefined;
  }>({
    patientId: -1,
  });
  const { Track } = useTracking<DeepPartial<BaseEventSchema>>({
    user_information: {
      auction_id: auctionId ?? null,
      consultation_id: consultationId ?? null,
      patient_id: patientId,
    },
  });

  const { activeRoles } = useSenderAccountContext();
  const hasDoctorDashboardOnly = hasAccessToDoctorDashboard(activeRoles);

  const [queryProgress, patient] = useGetPatientQuery({
    decrypt: true,
    patientId,
  });

  return (
    <Track>
      <div
        className="flex min-h-below-navbar flex-col bg-gray-50"
        data-testid="patient-route-layout"
      >
        <Result
          data={patient}
          ErrorComponent={
            <RedirectButton
              label={translations.actions.backToDashboard}
              url={
                hasDoctorDashboardOnly
                  ? doctorDashboardRedirectPath
                  : defaultRedirectPath
              }
            />
          }
          getErrorLabel={(translations) => translations.patient.errorLoading}
          id="patient-route-patient"
          queryProgress={queryProgress}
        >
          {(patient) => (
            <PatientEncryptionProvider patient={patient}>
              <Suspense fallback={<SpinnerPage id="patient-route-content" />}>
                {hasDoctorDashboardOnly ? (
                  <Outlet context={{ auction: undefined, patient }} />
                ) : !patient.auctions?.length ? (
                  <NoAuctionsRedirectToNewAuction
                    externalId={patient?.external_id}
                    patientId={patient?.id}
                  />
                ) : !auctionId ? (
                  <Navigate
                    replace
                    to={`/app/clinic/dashboard/patient/${patientId}/auction/${patient.auctions[0].id}/${PATIENT_TAB_SEARCH}?${searchParams}`}
                  />
                ) : (
                  <Outlet context={{ patient }} />
                )}
              </Suspense>
            </PatientEncryptionProvider>
          )}
        </Result>
      </div>
    </Track>
  );
}
